import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionsAPI, companiesAPI } from '../../services/api';
import { Subscription, CreateSubscriptionDto, UpdateSubscriptionDto, SubscriptionStatus, PaymentStatus, PaymentMethod, Company, Currency } from '../../types';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  MapPinIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import { toastSuccess } from '../../utils/toast';

// Ensure this file is treated as a module under isolatedModules
export {};

const SubscriptionForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyCurrencies, setCompanyCurrencies] = useState<Currency[]>([]);
  const [calculatedPrice, setCalculatedPrice] = useState<{ totalAmount: number; activeLocations: number } | null>(null);

  const [formData, setFormData] = useState<CreateSubscriptionDto>({
    companyId: '',
    basePrice: 0,
    pricePerLocation: 0,
    activeLocations: 0,
    totalAmount: 0,
    status: SubscriptionStatus.PENDING,
    paymentStatus: PaymentStatus.PENDING,
    paymentMethod: PaymentMethod.CASH,
    startDate: '',
    endDate: '',
    notes: '',
    adminNotes: '',
  });

  useEffect(() => {
    loadCompanies();
    if (id) {
      loadFormData();
    }
  }, [id]);

  const loadCompanies = async () => {
    try {
      const companiesData = await companiesAPI.getAll();
      setCompanies(companiesData);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const loadFormData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const subscription = await subscriptionsAPI.getById(id);
      setFormData({
        companyId: subscription.companyId,
        basePrice: subscription.basePrice,
        pricePerLocation: subscription.pricePerLocation,
        activeLocations: subscription.activeLocations,
        totalAmount: subscription.totalAmount,
        status: subscription.status,
        paymentStatus: subscription.paymentStatus,
        paymentMethod: subscription.paymentMethod,
        startDate: new Date(subscription.startDate).toISOString().split('T')[0], // Convert to YYYY-MM-DD format
        endDate: new Date(subscription.endDate).toISOString().split('T')[0],
        notes: subscription.notes || '',
        adminNotes: subscription.adminNotes || '',
      });
      
      // Set selected company
      const company = companies.find(c => c.id === subscription.companyId);
      if (company) {
        setSelectedCompany(company);
        try {
          const fullCompany = await companiesAPI.getById(company.id);
          setCompanyCurrencies(fullCompany.currencies || []);
        } catch (e) {
          console.error('Failed to load company currencies', e);
        }
      }
    } catch (err) {
      setError('Failed to load subscription');
      console.error('Error loading subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const companyId = e.target.value;
    setFormData(prev => ({
      ...prev,
      companyId,
    }));
    
    const company = companies.find(c => c.id === companyId);
    setSelectedCompany(company || null);
    if (company) {
      // Load company currencies
      companiesAPI.getById(company.id)
        .then(fullCompany => setCompanyCurrencies(fullCompany.currencies || []))
        .catch(err => console.error('Failed to load company currencies', err));
    } else {
      setCompanyCurrencies([]);
    }
    setCalculatedPrice(null);
  };

  const calculatePrice = async () => {
    if (!formData.companyId || formData.basePrice <= 0 || formData.pricePerLocation <= 0) {
      setError('Please select a company and enter valid pricing');
      return;
    }

    try {
      const result = await subscriptionsAPI.calculateSubscriptionPrice(
        formData.companyId,
        formData.basePrice,
        formData.pricePerLocation
      );
      setCalculatedPrice(result);
      setFormData(prev => ({
        ...prev,
        activeLocations: result.activeLocations,
        totalAmount: result.totalAmount,
      }));
      setError(null);
    } catch (err) {
      setError('Failed to calculate price');
      console.error('Error calculating price:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.companyId) {
      setError('Please select a company');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (id) {
        // Update existing subscription
        const updateData: UpdateSubscriptionDto = {
          basePrice: formData.basePrice,
          pricePerLocation: formData.pricePerLocation,
          activeLocations: formData.activeLocations,
          totalAmount: formData.totalAmount,
          status: formData.status,
          paymentStatus: formData.paymentStatus,
          paymentMethod: formData.paymentMethod,
          startDate: formData.startDate,
          endDate: formData.endDate,
          notes: formData.notes,
          adminNotes: formData.adminNotes,
        };
        await subscriptionsAPI.update(id, updateData);
      } else {
        // Create new subscription
        await subscriptionsAPI.create(formData);
      }
      toastSuccess(id ? 'Subscription updated successfully' : 'Subscription created successfully');
      navigate('/subscriptions');
    } catch (err) {
      setError('Failed to save subscription');
      console.error('Error saving subscription:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    const currencyCode = companyCurrencies?.[0]?.code || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/subscriptions')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? 'Edit Subscription' : 'Create New Subscription'}
            </h1>
            <p className="text-gray-600">Manage company subscription and billing</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Company and Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
              Company Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Company *</label>
                <select
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleCompanyChange}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedCompany && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Company Details</h4>
                  <div className="text-sm text-gray-600">
                    <p><strong>Name:</strong> {selectedCompany.name}</p>
                    <p><strong>Email:</strong> {selectedCompany.email}</p>
                    <p><strong>Phone:</strong> {selectedCompany.phone}</p>
                    {selectedCompany.city && selectedCompany.state && (
                      <p><strong>Location:</strong> {selectedCompany.city}, {selectedCompany.state}</p>
                    )}
                    {companyCurrencies.length > 0 && (
                      <p>
                        <strong>Currency:</strong> {companyCurrencies[0].code}
                        {companyCurrencies[0].symbol ? ` (${companyCurrencies[0].symbol})` : ''}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Pricing Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
              Pricing Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Base Price *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="basePrice"
                  value={formData.basePrice || ''}
                  placeholder="Enter base price"
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Price Per Location *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="pricePerLocation"
                  value={formData.pricePerLocation || ''}
                  placeholder="Enter price per location"
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <button
                type="button"
                onClick={calculatePrice}
                disabled={!formData.companyId || formData.basePrice <= 0 || formData.pricePerLocation <= 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CalculatorIcon className="h-4 w-4 mr-2" />
                Calculate Price
              </button>

              {calculatedPrice && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-green-900 mb-2">Calculated Price</h4>
                  <div className="text-sm text-green-800">
                    <p><strong>Active Locations:</strong> {calculatedPrice.activeLocations}</p>
                    <p><strong>Total Amount:</strong> {formatCurrency(calculatedPrice.totalAmount)}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Total Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="totalAmount"
                  value={formData.totalAmount || ''}
                  placeholder="Calculated or enter total amount"
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Period */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2" />
            Subscription Period
          </h3>
          
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date *</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">End Date *</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Status and Payment */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status and Payment</h3>
          
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Subscription Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value={SubscriptionStatus.PENDING}>Pending</option>
                <option value={SubscriptionStatus.ACTIVE}>Active</option>
                <option value={SubscriptionStatus.EXPIRED}>Expired</option>
                <option value={SubscriptionStatus.CANCELLED}>Cancelled</option>
                <option value={SubscriptionStatus.SUSPENDED}>Suspended</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Payment Status</label>
              <select
                name="paymentStatus"
                value={formData.paymentStatus}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value={PaymentStatus.PENDING}>Pending</option>
                <option value={PaymentStatus.PAID}>Paid</option>
                <option value={PaymentStatus.FAILED}>Failed</option>
                <option value={PaymentStatus.REFUNDED}>Refunded</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Payment Method</label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value={PaymentMethod.CASH}>Cash</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="Notes for the company..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Admin Notes</label>
              <textarea
                name="adminNotes"
                value={formData.adminNotes}
                onChange={handleInputChange}
                rows={3}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="Internal admin notes..."
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/subscriptions')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : id ? 'Update Subscription' : 'Create Subscription'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SubscriptionForm;
